import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (event instanceof Event && event.target instanceof HTMLElement) {
      event.preventDefault();
      if (event.target instanceof HTMLInputElement) {
        this.refs.quantityInput.value = event.target.value;
      }
      this.#onQuantityChange(event);
    } else {
      this.refs.quantityInput.value = String(event);
      this.#onQuantityChange(event);
    }
  }

  /**
   * Builds the quantity menu only when it is opened. This keeps large carts light
   * without changing the customer-facing dropdown.
   */
  prepareOptions() {
    const list = this.querySelector('[data-quantity-options]');
    if (!(list instanceof HTMLElement) || list.childElementCount > 0) return;

    const current = Number(this.refs.quantityInput.value) || 1;
    const configuredMaximum = Number(list.dataset.max) || 100;
    const maximum = Math.max(current, Math.min(configuredMaximum, 100));
    const fragment = document.createDocumentFragment();

    for (let quantity = 1; quantity <= maximum; quantity += 1) {
      const option = document.createElement('li');
      option.className = `cart__quantity-item${quantity === current ? ' is-disabled' : ''}`;
      option.setAttribute('on:click', `/setQuantity/${quantity}`);
      option.setAttribute('on:keydown', '/handleOptionKeydown');
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(quantity === current));
      option.tabIndex = quantity === current ? -1 : 0;
      option.textContent = String(quantity);
      fragment.appendChild(option);
    }

    list.appendChild(fragment);
  }

  /**
   * Lets keyboard customers choose a quantity from the custom listbox.
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();

    const option = event.target?.closest?.('[role="option"]');
    if (!(option instanceof HTMLElement)) return;
    this.setQuantity(Number(option.textContent));
    option.closest('details')?.removeAttribute('open');
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine), quantityInput.dataset.cartKey)
    );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = Number(quantityInput.min) || 0;
    const max = quantityInput.max ? Number(quantityInput.max) : 0;
    const newValue = Number(quantityInput.value);

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
